import { Button, Flex, Text, Select, Loader } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import classes from './useVacationEmployerChange.module.scss';
import { MODAL_SIZE_MEDIUM } from '@shared/ui/modal/constants';
import { useGetUsers } from '@entities/user';
import { useEffect, useMemo } from 'react';
import { useForm, zodResolver } from '@mantine/form';
import { useUpdateVacansyResponsible, type Vacancy } from '@entities/vacancy';
import { showNotification } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { $VacationEmployerChangeSchema } from './types';
import { IconChevronDown } from '@tabler/icons-react';
import { QueryKey } from '@shared/config';

const MODAL_ID = 'change-vacation-employer';

type UseVacationEmployerChangeComponentProps = {
  responsibleUserId: string;
  vacancyId: string;
};

const UseVacationEmployerChangeComponent = ({
  responsibleUserId,
  vacancyId,
}: UseVacationEmployerChangeComponentProps) => {
  const queryClient = useQueryClient();

  const { data: users, isLoading: isUsersLoading } = useGetUsers();

  const { mutate: updateVacancyResponsible, isPending } =
    useUpdateVacansyResponsible(vacancyId);

  const options = useMemo(() => {
    return users?.map(({ id, profile, role }) => ({
      value: id.toString(),
      label: `${profile?.firstName}, ${profile?.lastName} (${role?.displayName})`,
    }));
  }, [users]);

  const form = useForm({
    initialValues: {
      responsibleUserId,
    },
    validate: zodResolver($VacationEmployerChangeSchema),
  });

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleSubmit = (values: typeof form.values) => {
    updateVacancyResponsible(
      {
        responsibleUserId: Number(values.responsibleUserId),
      },
      {
        onSuccess: updatedUser => {
          queryClient.setQueryData(
            [QueryKey.GET_VACANCY, Number(vacancyId)],
            (oldData: Vacancy | undefined) => {
              if (!oldData) {
                return oldData;
              }
              return {
                ...oldData,
                responsibleUser: updatedUser,
                responsibleUserId: updatedUser.id,
              };
            }
          );

          closeModal(MODAL_ID);
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Ошибка при обновлении ответственного сотрудника',
          });

          closeModal(MODAL_ID);
        },
      }
    );
  };

  useEffect(() => {
    if (!isUsersLoading && responsibleUserId) {
      form.setFieldValue('responsibleUserId', responsibleUserId);
    }
  }, [isUsersLoading]);

  return (
    <Flex w='100%' flex={1}>
      <form className={classes.form} onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction='column' gap={16} pb={16}>
          <Text c='tertiary.7' size='sm'>
            Выберите ответственного рекрутера для вакансии
          </Text>

          <Select
            w='100%'
            variant='filled'
            data={options}
            key={form.key('responsibleUserId')}
            {...form.getInputProps('responsibleUserId')}
            error={form.errors.responsibleUserId}
            rightSection={
              isUsersLoading ? (
                <Loader size='xs' />
              ) : (
                <IconChevronDown
                  size={20}
                  color='var(--mantine-color-primary-7)'
                />
              )
            }
          />
        </Flex>

        <Flex gap={8} mt={12}>
          <Button variant='gray' onClick={handleClose}>
            Отмена
          </Button>
          <Button type='submit' loading={isPending}>
            Сохранить
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};

export const useVacationEmployerChange = () => {
  const openChangeVacationEmployer = (
    responsibleUserId: string,
    vacancyId: string
  ) =>
    openModal({
      modalId: MODAL_ID,
      title: 'Ответственный сотрудник',
      size: MODAL_SIZE_MEDIUM,
      children: (
        <UseVacationEmployerChangeComponent
          responsibleUserId={responsibleUserId}
          vacancyId={vacancyId}
        />
      ),
    });

  return { openChangeVacationEmployer };
};
